import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip the /auth/me check.
    // AuthCallback will exchange the session_id and set the cookie first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, totp_code = null, recovery_code = null) => {
    try {
      // If a TOTP code was provided, go straight to the 2FA endpoint
      if (totp_code || recovery_code) {
        const { data } = await axios.post(
          `${API}/api/auth/login-2fa`,
          { email, password, totp_code, recovery_code },
          { withCredentials: true }
        );
        setUser(data);
        // Hydrate `company` (plan_key + product_mode) via /auth/me so
        // the sidebar filter for pallets_only tenants applies immediately.
        try {
          const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
          setUser(me.data);
        } catch (_) { /* fall back to login payload */ }
        return { success: true };
      }
      // Otherwise try the base login
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      // Hydrate `company` (plan_key + product_mode) via /auth/me so
      // the sidebar filter for pallets_only tenants applies immediately.
      try {
        const me = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        setUser(me.data);
      } catch (_) { /* fall back to login payload */ }
      return { success: true };
    } catch (e) {
      // Did the server signal 2FA is required?
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.requires_2fa) {
        return { success: false, requires_2fa: true };
      }
      // If it was a status 403 with a string saying requires_2fa, handle that too
      if (e.response?.status === 403) {
        // The login endpoint may not know about 2FA — try the 2FA endpoint which does
        try {
          await axios.post(
            `${API}/api/auth/login-2fa`,
            { email, password },
            { withCredentials: true }
          );
        } catch (e2) {
          const d2 = e2.response?.data?.detail;
          if (d2 && typeof d2 === 'object' && d2.requires_2fa) {
            return { success: false, requires_2fa: true };
          }
          return { success: false, error: formatApiErrorDetail(d2) || e2.message };
        }
      }
      return { success: false, error: formatApiErrorDetail(detail) || e.message };
    }
  };

  const register = async (email, password, name, role = 'viewer') => {
    try {
      const { data } = await axios.post(
        `${API}/api/auth/register`,
        { email, password, name, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Ignore errors
    }
    setUser(false);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager' || user?.role === 'admin',
    isPicker: user?.role === 'picker',
    isPacker: user?.role === 'packer',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
